// TelcoTrack — SPOJKOVISTE optional „Bod záfuku“ (metadata `spojkovisteBodZafuku`).
// Fail-closed: type is only explicit OK / Svazek MT (legacy single-MT still parses).
// When enabled on a *standalone* spojkoviště (no linked BUDOVA), a záfuk source
// (ROZVADEC **or** SPOJKOVISTE) is required. Building-linked / odbočka flow already
// sets source via „Připojeno z“ per budova — the second picker is hidden and not required.

import {
  applyDerivedTypOkList,
  hasRequiredOkCableMaterial,
  isDerivedTypOkValue,
  OK_MATERIAL_REQUIRED_MESSAGE,
  resolveTypOkFromMaterials,
  type OkMaterialUsageLike,
} from './typ-ok-from-material.constants';
import {
  isRozvadecZafukKind,
  ROZVADEC_ZAFUK_PICKER_KINDS,
  type RozvadecZafukKind,
} from './rozvadec-zafuk.constants';
import { parseZafukMetaDouble, recomputeZafukLengthFromOkMetraz } from './zafuk-spec.constants';
import {
  createZafukSourceRef,
  normalizeZafukSourceRef,
  ZAFUK_SOURCE_LABEL,
  ZAFUK_SOURCE_REQUIRED_MESSAGE,
  zafukSourceLabel,
  zafukSourceToFlatFields,
  zafukSourceToLegacyFields,
  type ZafukSourceObjectType,
} from './zafuk-source.constants';

export const SPOJKOVISTE_BOD_ZAFUKU_METADATA_KEY = 'spojkovisteBodZafuku' as const;

/** Picker options — OK + svazek only (single MT removed from UI). */
export const SPOJKOVISTE_BOD_ZAFUKU_KINDS = ROZVADEC_ZAFUK_PICKER_KINDS;
export type SpojkovisteBodZafukuKind = RozvadecZafukKind;

/** Spojkoviště picker labels — OK vs Svazek MT (legacy single-MT label retained for INFORMACE). */
export const SPOJKOVISTE_BOD_ZAFUKU_KIND_LABELS: Record<SpojkovisteBodZafukuKind, string> = {
  opticka_vlna: 'OK',
  mikrotrubicky_svazek: 'Svazek MT',
  mikrotrubicka: 'Mikrotrubička (MT)',
};

export const SPOJKOVISTE_BOD_ZAFUKU_KIND_UNSET_LABEL = 'Zvolte typ';

export const SPOJKOVISTE_BOD_ZAFUKU_KIND_REQUIRED_MESSAGE =
  'Zvolte typ (OK nebo Svazek MT).';

export const SPOJKOVISTE_BOD_ZAFUKU_METRAZ_REQUIRED_MESSAGE =
  'Zadejte platnou metráž startu a konce.';

export const SPOJKOVISTE_BOD_ZAFUKU_TYP_OK_REQUIRED_MESSAGE = 'Vyberte typ OK.';

/** @deprecated Prefer ZAFUK_SOURCE_REQUIRED_MESSAGE — source may be ROZVADEC or SPOJKOVISTE. */
export const SPOJKOVISTE_BOD_ZAFUKU_ROZVADEC_REQUIRED_MESSAGE = ZAFUK_SOURCE_REQUIRED_MESSAGE;

export const SPOJKOVISTE_BOD_ZAFUKU_ROZVADEC_LABEL = ZAFUK_SOURCE_LABEL;

export const SPOJKOVISTE_BOD_ZAFUKU_CHECKBOX_LABEL = 'Bod záfuku';

/** Options for field / full validation of Bod záfuku. */
export type SpojkovisteBodZafukuFieldOptions = {
  /**
   * When false, skip required záfuk source (building-linked spojkoviště already has
   * „Připojeno z“). Default true = standalone bod záfuku.
   * Name kept for call-site compatibility; source may be ROZVADEC or SPOJKOVISTE.
   */
  requireSourceRozvadec?: boolean;
};

/** True only for standalone bod záfuku (no linked BUDOVA „Připojeno z“ slots). */
export function spojkovisteBodZafukuRequiresSourceRozvadec(
  hasLinkedBudovaSlots: boolean,
): boolean {
  return !hasLinkedBudovaSlots;
}

export interface SpojkovisteBodZafukuSpec {
  enabled: boolean;
  kind: SpojkovisteBodZafukuKind | null;
  typOk: string | null;
  okMetrazStartM: number | null;
  okMetrazKonecM: number | null;
  /**
   * Legacy ROZVADEC-only mirror — empty when source is SPOJKOVISTE
   * (see `zafukSourceToLegacyFields`). Prefer `zafukSourceObjectId` / Type / Name.
   */
  zafukZRozvadecObjectId: string | null;
  zafukZRozvadecName: string | null;
  /** Neutral záfuk source (ROZVADEC or SPOJKOVISTE). */
  zafukSourceObjectId?: string | null;
  zafukSourceObjectType?: ZafukSourceObjectType | null;
  zafukSourceName?: string | null;
}

export function emptySpojkovisteBodZafukuSpec(): SpojkovisteBodZafukuSpec {
  return {
    enabled: false,
    kind: null,
    typOk: null,
    okMetrazStartM: null,
    okMetrazKonecM: null,
    zafukZRozvadecObjectId: null,
    zafukZRozvadecName: null,
    zafukSourceObjectId: null,
    zafukSourceObjectType: null,
    zafukSourceName: null,
  };
}

export function spojkovisteBodZafukuKindLabel(
  kind: SpojkovisteBodZafukuKind | null | undefined,
): string {
  return isRozvadecZafukKind(kind)
    ? SPOJKOVISTE_BOD_ZAFUKU_KIND_LABELS[kind]
    : SPOJKOVISTE_BOD_ZAFUKU_KIND_UNSET_LABEL;
}

export function spojkovisteBodZafukuLengthM(
  spec: Pick<SpojkovisteBodZafukuSpec, 'okMetrazStartM' | 'okMetrazKonecM'>,
): number | null {
  return recomputeZafukLengthFromOkMetraz(spec.okMetrazStartM, spec.okMetrazKonecM);
}

function parseKind(raw: unknown): SpojkovisteBodZafukuKind | null {
  const s = typeof raw === 'string' ? raw.trim() : '';
  return isRozvadecZafukKind(s) ? s : null;
}

function parseOptionalId(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const id = raw.trim();
  return id || null;
}

/** Apply typ OK from selected / used cable materials — only when kind is explicit OK. */
export function applyTypOkFromMaterialsToSpojkovisteBodZafuku(
  spec: SpojkovisteBodZafukuSpec,
  materials: readonly OkMaterialUsageLike[] | null | undefined,
): SpojkovisteBodZafukuSpec {
  if (!spec.enabled || spec.kind !== 'opticka_vlna') {
    return { ...spec, typOk: spec.kind === 'opticka_vlna' ? spec.typOk : null };
  }
  const resolved = resolveTypOkFromMaterials(materials);
  const assigned = applyDerivedTypOkList(1, resolved.types);
  const derived = assigned[0];
  return {
    ...spec,
    typOk: derived ?? (isDerivedTypOkValue(spec.typOk) ? spec.typOk : null),
  };
}

function asBodZafukuRecord(raw: unknown): Record<string, unknown> | null {
  if (!raw) return null;
  if (typeof raw === 'string') {
    const text = raw.trim();
    if (!text.startsWith('{')) return null;
    try {
      const parsed = JSON.parse(text) as unknown;
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  return null;
}

function parseEnabledFlag(raw: unknown): boolean | null {
  if (raw === true || raw === 'true' || raw === 1 || raw === '1') return true;
  if (raw === false || raw === 'false' || raw === 0 || raw === '0') return false;
  return null;
}

function sourceFieldsFromSpecLike(raw: Record<string, unknown> | SpojkovisteBodZafukuSpec): {
  zafukZRozvadecObjectId: string | null;
  zafukZRozvadecName: string | null;
  zafukSourceObjectId: string | null;
  zafukSourceObjectType: ZafukSourceObjectType | null;
  zafukSourceName: string | null;
} {
  const ref = normalizeZafukSourceRef(raw);
  const flat = zafukSourceToFlatFields(ref);
  const legacy = zafukSourceToLegacyFields(ref);
  return {
    zafukZRozvadecObjectId: legacy.zafukZRozvadecObjectId,
    zafukZRozvadecName: legacy.zafukZRozvadecName,
    zafukSourceObjectId: flat.zafukSourceObjectId,
    zafukSourceObjectType: flat.zafukSourceObjectType,
    zafukSourceName: flat.zafukSourceName,
  };
}

export function normalizeSpojkovisteBodZafuku(
  sources: {
    spojkovisteBodZafuku?: unknown;
    metadata?: Record<string, unknown> | null;
  },
): SpojkovisteBodZafukuSpec {
  const raw = sources.spojkovisteBodZafuku ?? sources.metadata?.[SPOJKOVISTE_BOD_ZAFUKU_METADATA_KEY];
  const o = asBodZafukuRecord(raw);
  if (!o) return emptySpojkovisteBodZafukuSpec();
  const kind = parseKind(o['kind']);
  const start = parseZafukMetaDouble(o['okMetrazStartM'] ?? o['zafukOkMetrazStartM']);
  const end = parseZafukMetaDouble(o['okMetrazKonecM'] ?? o['zafukOkMetrazKonecM']);
  const explicit = parseEnabledFlag(o['enabled']);
  if (explicit === false) return emptySpojkovisteBodZafukuSpec();
  const source = sourceFieldsFromSpecLike(o);
  const enabled =
    explicit === true ||
    kind != null ||
    start != null ||
    end != null ||
    !!source.zafukSourceObjectId;
  if (!enabled) return emptySpojkovisteBodZafukuSpec();
  const typOkRaw = typeof o['typOk'] === 'string' ? o['typOk'].trim() : '';
  return {
    enabled: true,
    kind,
    typOk: kind === 'opticka_vlna' && typOkRaw && isDerivedTypOkValue(typOkRaw) ? typOkRaw : null,
    okMetrazStartM: start,
    okMetrazKonecM: end,
    ...source,
  };
}

/**
 * Reconstruct Bod záfuku from dedicated záfuk columns when metadata blob is missing
 * (bbox / stale map cache). Presence of start+end metráž is the persist signal.
 *
 * Kind inference (columns do not store `kind`):
 * - `zafukTypOk` present → OK (`opticka_vlna`)
 * - otherwise → Svazek MT (`mikrotrubicky_svazek`) — backend clears typOk for MT on persist
 */
export function normalizeSpojkovisteBodZafukuFromZafukColumns(sources: {
  zafukOkMetrazStartM?: unknown;
  zafukOkMetrazKonecM?: unknown;
  zafukTypOk?: unknown;
  zafukZRozvadecObjectId?: unknown;
  zafukZRozvadecName?: unknown;
  zafukSourceObjectId?: unknown;
  zafukSourceObjectType?: unknown;
  zafukSourceName?: unknown;
  pripojovanyZafouknutoZRozvadecObjectId?: unknown;
  pripojovanyZafouknutoZRozvadecName?: unknown;
} | null | undefined): SpojkovisteBodZafukuSpec {
  if (!sources) return emptySpojkovisteBodZafukuSpec();
  const start = parseZafukMetaDouble(sources.zafukOkMetrazStartM);
  const end = parseZafukMetaDouble(sources.zafukOkMetrazKonecM);
  if (start == null || end == null) return emptySpojkovisteBodZafukuSpec();
  const typOkRaw = typeof sources.zafukTypOk === 'string' ? sources.zafukTypOk.trim() : '';
  const isOk = !!(typOkRaw && isDerivedTypOkValue(typOkRaw));
  const kind: SpojkovisteBodZafukuKind = isOk ? 'opticka_vlna' : 'mikrotrubicky_svazek';
  const source = sourceFieldsFromSpecLike({
    zafukSourceObjectId: sources.zafukSourceObjectId,
    zafukSourceObjectType: sources.zafukSourceObjectType,
    zafukSourceName: sources.zafukSourceName,
    zafukZRozvadecObjectId: sources.zafukZRozvadecObjectId,
    zafukZRozvadecName: sources.zafukZRozvadecName,
    pripojovanyZafouknutoZRozvadecObjectId: sources.pripojovanyZafouknutoZRozvadecObjectId,
    pripojovanyZafouknutoZRozvadecName: sources.pripojovanyZafouknutoZRozvadecName,
  } as Record<string, unknown>);
  return {
    enabled: true,
    kind,
    typOk: isOk ? typOkRaw : null,
    okMetrazStartM: start,
    okMetrazKonecM: end,
    ...source,
  };
}

/**
 * Persist shape. Unchecked checkbox stores `{ enabled: false }` only — no leftover
 * kind / typ OK / metráž / source that could re-impose OK material.
 * Keeps neutral `zafukSource*` so SPOJKOVISTE map picks survive serialize → validate.
 */
export function serializeSpojkovisteBodZafukuForCommit(
  spec: SpojkovisteBodZafukuSpec | null | undefined,
): SpojkovisteBodZafukuSpec {
  if (!spec?.enabled) return emptySpojkovisteBodZafukuSpec();
  const kind = isRozvadecZafukKind(spec.kind) ? spec.kind : null;
  const source = sourceFieldsFromSpecLike(spec as SpojkovisteBodZafukuSpec & Record<string, unknown>);
  return {
    enabled: true,
    kind,
    typOk:
      kind === 'opticka_vlna' && spec.typOk?.trim() && isDerivedTypOkValue(spec.typOk.trim())
        ? spec.typOk.trim()
        : null,
    okMetrazStartM: parseZafukMetaDouble(spec.okMetrazStartM),
    okMetrazKonecM: parseZafukMetaDouble(spec.okMetrazKonecM),
    ...source,
  };
}

/** True when Bod záfuku is OK (optical) — ≥1 OK cable in materiál is required. MT bundle is not. */
export function spojkovisteBodZafukuNeedsOpticalCableMaterial(
  spec: SpojkovisteBodZafukuSpec | null | undefined,
): boolean {
  return spec?.enabled === true && spec.kind === 'opticka_vlna';
}

export function areSpojkovisteBodZafukuFieldsValid(
  spec: SpojkovisteBodZafukuSpec | null | undefined,
  materials?: readonly OkMaterialUsageLike[] | null,
  options?: SpojkovisteBodZafukuFieldOptions,
): boolean {
  return describeSpojkovisteBodZafukuFieldsFailure(spec, materials, options) == null;
}

/**
 * Specifikace field gate (kind, metráž, záfuk source).
 * OK kabel in materiál is **not** required here — that is a phase-confirm gate
 * (`describeSpojkovisteBodZafukuValidationFailure`) and only for kind OK.
 * Source may be ROZVADEC or SPOJKOVISTE — never require legacy rozvaděč-only id.
 */
export function describeSpojkovisteBodZafukuFieldsFailure(
  spec: SpojkovisteBodZafukuSpec | null | undefined,
  materials?: readonly OkMaterialUsageLike[] | null,
  options?: SpojkovisteBodZafukuFieldOptions,
): string | null {
  const normalized = serializeSpojkovisteBodZafukuForCommit(spec);
  if (!normalized.enabled) return null;
  const withTypOk = applyTypOkFromMaterialsToSpojkovisteBodZafuku(normalized, materials);
  if (!isRozvadecZafukKind(withTypOk.kind)) {
    return SPOJKOVISTE_BOD_ZAFUKU_KIND_REQUIRED_MESSAGE;
  }
  const lengthM = spojkovisteBodZafukuLengthM(withTypOk);
  if (lengthM == null || !Number.isFinite(lengthM) || lengthM < 0) {
    return SPOJKOVISTE_BOD_ZAFUKU_METRAZ_REQUIRED_MESSAGE;
  }
  const requireSource = options?.requireSourceRozvadec !== false;
  if (requireSource && !normalizeZafukSourceRef(withTypOk)) {
    return ZAFUK_SOURCE_REQUIRED_MESSAGE;
  }
  return null;
}

/** Full gate including OK cable when type is OK (status change / commit). */
export function describeSpojkovisteBodZafukuValidationFailure(
  spec: SpojkovisteBodZafukuSpec | null | undefined,
  materials?: readonly OkMaterialUsageLike[] | null,
  options?: SpojkovisteBodZafukuFieldOptions,
): string | null {
  const fields = describeSpojkovisteBodZafukuFieldsFailure(spec, materials, options);
  if (fields) return fields;
  const normalized = serializeSpojkovisteBodZafukuForCommit(spec);
  if (!spojkovisteBodZafukuNeedsOpticalCableMaterial(normalized)) return null;
  if (!hasRequiredOkCableMaterial(materials)) {
    return OK_MATERIAL_REQUIRED_MESSAGE;
  }
  return null;
}

export function areSpojkovisteBodZafukuValid(
  spec: SpojkovisteBodZafukuSpec | null | undefined,
  materials?: readonly OkMaterialUsageLike[] | null,
  options?: SpojkovisteBodZafukuFieldOptions,
): boolean {
  return describeSpojkovisteBodZafukuValidationFailure(spec, materials, options) == null;
}

/**
 * When Bod záfuku has no own source but building flow has exactly one
 * „Připojeno z“ selection, inherit it for persistence / display consistency.
 */
export function withInheritedSpojkovisteBodZafukuSourceRozvadec(
  spec: SpojkovisteBodZafukuSpec | null | undefined,
  budovaSourceRozvadecs: ReadonlyArray<
    { id?: string | null; name?: string | null } | null | undefined
  >,
): SpojkovisteBodZafukuSpec {
  const normalized = serializeSpojkovisteBodZafukuForCommit(spec);
  if (!normalized.enabled || normalizeZafukSourceRef(normalized)) {
    return normalized;
  }
  const unique = new Map<string, string>();
  for (const s of budovaSourceRozvadecs) {
    const id = (s?.id ?? '').trim();
    if (!id) continue;
    if (!unique.has(id)) unique.set(id, (s?.name ?? '').trim() || id);
  }
  if (unique.size !== 1) return normalized;
  const [id, name] = [...unique.entries()][0]!;
  const ref = createZafukSourceRef(id, 'ROZVADEC', name);
  const source = sourceFieldsFromSpecLike({
    ...zafukSourceToFlatFields(ref),
    ...zafukSourceToLegacyFields(ref),
  });
  return { ...normalized, ...source };
}

export function serializeSpojkovisteBodZafukuForBaseline(
  spec: SpojkovisteBodZafukuSpec | null | undefined,
): string {
  return JSON.stringify(serializeSpojkovisteBodZafukuForCommit(spec));
}

export type SpojkovisteBodZafukuInfoRow = { label: string; value: string };

/**
 * INFORMACE / ZÁFUK — rows only when Bod záfuku is enabled.
 * Omits the redundant „Bod záfuku: Ano“ checkbox echo (edit UI keeps the checkbox).
 */
export function buildSpojkovisteBodZafukuInfoRows(
  spec: SpojkovisteBodZafukuSpec | null | undefined,
): SpojkovisteBodZafukuInfoRow[] {
  const normalized = serializeSpojkovisteBodZafukuForCommit(spec);
  if (!normalized.enabled) return [];
  const rows: SpojkovisteBodZafukuInfoRow[] = [
    { label: 'Typ', value: spojkovisteBodZafukuKindLabel(normalized.kind) },
  ];
  if (normalized.kind === 'opticka_vlna') {
    const typOk = (normalized.typOk ?? '').trim();
    if (typOk && isDerivedTypOkValue(typOk)) {
      rows.push({ label: 'Typ OK', value: typOk });
    }
  }
  const start = parseZafukMetaDouble(normalized.okMetrazStartM);
  if (start != null) {
    rows.push({ label: 'Metráž startu', value: `${formatMetraz(start)} m` });
  }
  const end = parseZafukMetaDouble(normalized.okMetrazKonecM);
  if (end != null) {
    rows.push({ label: 'Metráž konce', value: `${formatMetraz(end)} m` });
  }
  const lengthM = spojkovisteBodZafukuLengthM(normalized);
  if (lengthM != null) {
    rows.push({ label: 'Délka záfuku', value: `${formatMetraz(lengthM)} m` });
  }
  const ref = normalizeZafukSourceRef(normalized);
  if (ref) {
    rows.push({ label: ZAFUK_SOURCE_LABEL, value: zafukSourceLabel(ref) });
  }
  return rows;
}

function formatMetraz(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}
